import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/store/i18n';

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
}

function pageRange(current: number, total: number, span = 1): (number | 'gap')[] {
  const out: (number | 'gap')[] = [];
  const add = (n: number) => out.push(n);
  add(1);
  if (current - span > 2) out.push('gap');
  for (let i = Math.max(2, current - span); i <= Math.min(total - 1, current + span); i++) add(i);
  if (current + span < total - 1) out.push('gap');
  if (total > 1) add(total);
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange }: Props) {
  const t = useT();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3">
      <div className="text-xs text-slate-500">
        {t('pagination.showing')} <strong className="text-slate-700">{from.toLocaleString('th-TH')}-{to.toLocaleString('th-TH')}</strong>
        {' '}{t('pagination.of')} <strong className="text-slate-700">{total.toLocaleString('th-TH')}</strong> {t('pagination.items')}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('pagination.prev_aria')}
          className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pageRange(page, totalPages).map((p, i) =>
          p === 'gap' ? (
            <span key={`g${i}`} className="px-1.5 text-slate-400 text-xs">…</span>
          ) : (
            <button
              type="button"
              key={p}
              onClick={() => onPageChange(p)}
              className={cn(
                'min-w-[32px] h-8 px-2.5 rounded-md text-sm font-medium',
                p === page
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              )}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('pagination.next_aria')}
          className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
